import { firstValueFrom, BehaviorSubject } from "rxjs";
import { Jsonify } from "type-fest";

import { UserApiTokenRequest } from "@vowkey/common/auth/models/request/identity-token/user-api-token.request";
import { IdentityTokenResponse } from "@vowkey/common/auth/models/response/identity-token.response";
import { VaultTimeoutAction } from "@vowkey/common/key-management/vault-timeout";
import { UserId } from "@vowkey/common/types/guid";
import { UnlockService } from "@vowkey/unlock";

import { UserApiLoginCredentials } from "../models/domain/login-credentials";
import { CacheData } from "../services/login-strategies/login-strategy.state";

import { LoginStrategy, LoginStrategyData } from "./login.strategy";

export class UserApiLoginStrategyData implements LoginStrategyData {
  readonly tokenRequest: UserApiTokenRequest;

  constructor(fields: UserApiLoginStrategyData) {
    this.tokenRequest = fields.tokenRequest;
  }

  static fromJSON(obj: Jsonify<UserApiLoginStrategyData>): UserApiLoginStrategyData {
    return new UserApiLoginStrategyData({
      tokenRequest: UserApiTokenRequest.fromJSON(obj.tokenRequest),
    });
  }
}

export class UserApiLoginStrategy extends LoginStrategy<UserApiLoginStrategyData> {
  protected cache: BehaviorSubject<UserApiLoginStrategyData | undefined>;

  constructor(
    data: UserApiLoginStrategyData | undefined,
    private unlockService: UnlockService,
    ...sharedDeps: ConstructorParameters<typeof LoginStrategy>
  ) {
    super(...sharedDeps);

    this.cache = new BehaviorSubject(data);
  }

  override async logIn(credentials: UserApiLoginCredentials) {
    const tokenRequest = new UserApiTokenRequest(
      credentials.clientId,
      credentials.clientSecret,
      await this.buildTwoFactor(),
      await this.buildDeviceRequest(),
    );
    this.cache.next(new UserApiLoginStrategyData({ tokenRequest }));

    const [authResult] = await this.startLogIn();
    return authResult;
  }

  protected override async unlock(response: IdentityTokenResponse, userId: UserId): Promise<void> {
    if (response.canUnlockWithKeyConnector()) {
      await this.unlockService.unlockWithKeyConnector(
        userId,
        response.intoKeyConnectorUnlockData(),
      );
    }
  }

  // Overridden to save client ID and secret to token service
  protected async saveAccountInformation(tokenResponse: IdentityTokenResponse): Promise<UserId> {
    const userId = await super.saveAccountInformation(tokenResponse);

    const vaultTimeoutAction = await firstValueFrom(
      this.vaultTimeoutSettingsService.getVaultTimeoutActionByUserId$(userId),
    );
    const vaultTimeout = await firstValueFrom(
      this.vaultTimeoutSettingsService.getVaultTimeoutByUserId$(userId),
    );

    const { tokenRequest } = this.getLoginStrategyDataOrThrow();

    await this.tokenService.setClientId(
      tokenRequest.clientId,
      vaultTimeoutAction as VaultTimeoutAction,
      vaultTimeout,
    );
    await this.tokenService.setClientSecret(
      tokenRequest.clientSecret,
      vaultTimeoutAction as VaultTimeoutAction,
      vaultTimeout,
    );
    return userId;
  }

  exportCache(): CacheData {
    return {
      userApiKey: this.cache.value,
    };
  }
}
